import * as React from 'react';
import * as LabelPrimitive from '@radix-ui/react-label';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '../../lib/utils';
import { containerDirection, documentDirection } from '../../shared/utils/bidi';

const labelVariants = cva(
  'text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70'
);

/**
 * The required marker itself — the ONE place the asterisk is spelled.
 *
 * Prefer `<Label required>`, which renders this for you as a sibling of the
 * label element (see the comment on the `required` prop). Reach for
 * `RequiredMarker` directly only where the thing being marked is not a `Label`
 * and cannot become one: a `<legend>` over a radio group, a `<TableHead>`
 * column header in an editable grid, an import-mapping row. Anywhere else,
 * a hand-rolled `<span className="text-destructive">*</span>` is a lint error
 * (`tools/check_required_field_marker.py`).
 *
 * `ms-1` is logical, not `ml-1`, so it trails the text under RTL too;
 * `aria-hidden` because the semantic belongs on the control's `aria-required`,
 * not on a decorative glyph.
 */
const RequiredMarker = () => (
  <span className="text-destructive ms-1" aria-hidden="true">
    *
  </span>
);
RequiredMarker.displayName = 'RequiredMarker';

const Label = React.forwardRef<
  React.ElementRef<typeof LabelPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof LabelPrimitive.Root> &
    VariantProps<typeof labelVariants> & {
      /**
       * Renders the required marker. The marker was hand-rolled at ~150 call
       * sites in five different spellings (`ms-1`, `ml-1`, `ms-0.5`, a bare
       * span, and a literal ` *` in the label text) — which is exactly how a
       * required field ends up with no marker at all. Pass this instead.
       *
       * This is the VISUAL half only. Pair it with `aria-required` on the
       * control, which is what assistive tech actually reads — see the marker's
       * own comment below.
       */
      required?: boolean;
    }
>(({ className, required, children, ...props }, ref) => {
  // Resolved through the leaf `shared/utils/bidi` rather than the direction
  // CONTEXT: `Label` sits in almost every component's import graph, and the
  // context module reaches `i18n/config`, whose body runs i18n init.
  const contentDir = containerDirection(children, documentDirection());

  return (
  <>
    <LabelPrimitive.Root
      ref={ref}
      // Resolved from the children when they are readable as text, and OMITTED
      // otherwise so the label inherits the document direction.
      //
      // This carried a blanket `dir="auto"` from dd4b6cac67 (2026-06-22) until
      // WI cf3fc9a4. First-strong read the leading Latin token of every Hebrew
      // label opening with a brand or module name — "PanLuma הוא מקור האמת" laid
      // out LTR, PanLuma on the wrong side of its own sentence — and because
      // `Label` is also the clickable wrapper around a whole card (see
      // `EntitySyncPolicyField`), one leading word flipped the card's hint text
      // with it. That card is precisely the unreadable-children case, which is
      // why the fallback here is the document direction and never `'auto'`.
      //
      // Resolving readable children rather than always inheriting keeps the net
      // this primitive used to provide: a call site rendering a raw entity name
      // in a `<Label>` stays correct without knowing about `<BidiText>`. Word
      // majority answers translated copy and entity names alike. `props.dir`
      // still overrides, since it spreads after this.
      dir={contentDir}
      className={cn(labelVariants(), className)}
      {...props}
    >
      {children}
    </LabelPrimitive.Root>
    {required && (
      // Deliberately a SIBLING of the label, not a child. Inside it, the
      // asterisk joins the label's text content, which renames the field to
      // "Name*" — `getByLabelText('Name')` then stops matching (five tests in
      // modules/sales went red on that, and exact-match is the common spelling
      // across the ~150 sites still to migrate) and screen readers announce
      // "Name star". Outside, the accessible name is untouched.
      //
      <RequiredMarker />
    )}
  </>
  );
});
Label.displayName = LabelPrimitive.Root.displayName;

export { Label, RequiredMarker };
